// LevelUpCelebration — full-screen one-shot moment when the user hits a new
// level. Designed to feel like a real milestone vs. the lightweight per-points
// toasts: bigger confetti burst, layered WebAudio chime, named tier, and a
// dismissable card with a single CTA.
//
// Why WebAudio instead of an <audio> tag:
//   - Zero asset weight, zero hosting/licensing concerns.
//   - We can synth a warm two-note chime that matches the brand tone.
//   - Browsers block autoplay without user gesture; level-ups always follow
//     a user action (chat send, image gen, quest complete) so the gesture
//     requirement is naturally satisfied.
//
// Respects prefers-reduced-motion (skips confetti) and a one-time mute toggle
// stored in localStorage so users who hate sound never hear it twice.
import { useEffect, useState } from "react";
import { Sparkles, Trophy, Volume2, VolumeX, X } from "lucide-react";
import { onLevelUp, type LevelUpDetail } from "@/lib/levelUpBus";

const MUTE_KEY = "hackerlab:level-up-muted";
const AUTO_DISMISS_MS = 5500;

// Synthesize a short two-note chime (perfect fifth, ~C5→G5) using WebAudio.
// Two oscillators with a quick attack/decay envelope. ~700ms total. Keeps
// the bundle lean and lets us tune the vibe without sourcing a sound file.
const playChime = () => {
  try {
    const Ctx =
      (window.AudioContext as typeof AudioContext | undefined) ||
      ((window as unknown as { webkitAudioContext?: typeof AudioContext })
        .webkitAudioContext as typeof AudioContext | undefined);
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;

    const playNote = (freq: number, start: number, dur: number, gain = 0.18) => {
      const osc = ctx.createOscillator();
      const env = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      env.gain.setValueAtTime(0.0001, now + start);
      env.gain.exponentialRampToValueAtTime(gain, now + start + 0.04);
      env.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      osc.connect(env).connect(ctx.destination);
      osc.start(now + start);
      osc.stop(now + start + dur + 0.05);
    };

    // C5, E5, G5 — a quick rising arpeggio.
    playNote(523.25, 0, 0.35);
    playNote(659.25, 0.12, 0.35);
    playNote(783.99, 0.24, 0.55, 0.22);

    // Close the context shortly after to free resources.
    setTimeout(() => ctx.close().catch(() => {}), 1200);
  } catch {
    // Audio is decorative; never let it break the celebration.
  }
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const LevelUpCelebration = () => {
  const [event, setEvent] = useState<LevelUpDetail | null>(null);
  const [muted, setMuted] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(MUTE_KEY) === "1";
  });

  useEffect(() => {
    const off = onLevelUp((detail) => {
      setEvent(detail);
      if (!muted) playChime();
    });
    return off;
  }, [muted]);

  // Auto-dismiss so the page never gets stuck behind the overlay.
  useEffect(() => {
    if (!event) return;
    const t = setTimeout(() => setEvent(null), AUTO_DISMISS_MS);
    return () => clearTimeout(t);
  }, [event]);

  if (!event) return null;

  const reduced = prefersReducedMotion();
  // Slightly more flakes than the welcome confetti — this is the bigger moment.
  const flakeCount = reduced ? 0 : 60;

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    try {
      window.localStorage.setItem(MUTE_KEY, next ? "1" : "0");
    } catch {
      // ignore storage errors (private mode etc.)
    }
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={`Level up — Level ${event.level} ${event.tierLabel}`}
      className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none"
    >
      {/* Backdrop: subtle, non-blocking visually, but click-through. */}
      <div className="absolute inset-0 bg-background/60 backdrop-blur-sm pointer-events-auto" />

      {/* Confetti layer — pure CSS, no deps. */}
      {flakeCount > 0 && (
        <div aria-hidden className="absolute inset-0 overflow-hidden">
          {Array.from({ length: flakeCount }).map((_, i) => (
            <span
              key={i}
              className="absolute -top-6 w-2 h-3.5 rounded-sm animate-confetti"
              style={{
                left: `${(i * 100) / flakeCount}%`,
                backgroundColor: [
                  "hsl(var(--primary))",
                  "hsl(var(--accent))",
                  "hsl(var(--foreground))",
                ][i % 3],
                animationDelay: `${(i % 12) * 0.08}s`,
                animationDuration: `${2.2 + (i % 6) * 0.35}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Card */}
      <div
        className={`relative pointer-events-auto max-w-md w-[92vw] rounded-2xl border border-primary/50 bg-card/95 shadow-2xl p-7 text-center ${
          reduced ? "" : "animate-scale-in"
        }`}
        style={{ boxShadow: "0 0 60px hsl(var(--primary) / 0.35)" }}
      >
        <button
          type="button"
          onClick={() => setEvent(null)}
          aria-label="Dismiss"
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex justify-center">
          <div
            className={`relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary to-primary/60 ${
              reduced ? "" : "animate-pulse-glow"
            }`}
            style={{ boxShadow: "0 0 32px hsl(var(--primary) / 0.7)" }}
          >
            <Trophy className="w-8 h-8 text-primary-foreground" />
            <Sparkles className="absolute -top-1 -right-1 w-4 h-4 text-foreground" />
          </div>
        </div>

        <p className="mt-4 text-[11px] font-mono uppercase tracking-[0.2em] text-primary">
          Level Up
        </p>
        <h2 className="mt-1 font-mono font-extrabold text-3xl text-foreground">
          Level {event.level}
        </h2>
        <p className="mt-1 text-base text-foreground">
          You're now a{" "}
          <span className="font-mono font-bold text-primary">
            {event.tierLabel}
          </span>
          .
        </p>
        {event.pointsGained > 0 && (
          <p className="mt-2 text-xs font-mono text-muted-foreground">
            +{event.pointsGained.toLocaleString()} points earned
          </p>
        )}

        <div className="mt-6 flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setEvent(null)}
            className="px-5 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-xs font-mono font-semibold transition-colors"
          >
            Keep going →
          </button>
          <button
            type="button"
            onClick={toggleMute}
            aria-label={muted ? "Unmute level-up sound" : "Mute level-up sound"}
            title={muted ? "Sound off" : "Sound on"}
            className="p-2 rounded-md border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LevelUpCelebration;
